import type { ChartSeries } from '../../shared/charts/series';
import { formatMoney, type MoneyLanguage } from '../../shared/format/money';
import { formatTimestamp, type TimeLanguage } from '../../shared/format/time';
import type { DashboardTrends } from './types';

export type TrendMetric = 'playAmount' | 'transactionCount';

/**
 * Grouped, Latin-digit count formatting. Exported because `TrendChart` needs the same formatter for the
 * summed "Other" series it asks `AreaTrendChart` to build, and two definitions would drift.
 */
export function countFormat(language: MoneyLanguage): Intl.NumberFormat {
  return new Intl.NumberFormat(language === 'th' ? 'th-TH-u-nu-latn' : 'en-US-u-nu-latn');
}

/**
 * Compact magnitudes (1.2M, 450K) for a value axis shared by several currencies. Deliberately carries no
 * currency symbol: one axis cannot claim a unit that only some of its series use. Currency-labelled
 * amounts come from `formatMoney` in the tooltip and the accessible table.
 */
export function compactFormat(language: MoneyLanguage): Intl.NumberFormat {
  return new Intl.NumberFormat(language === 'th' ? 'th-TH-u-nu-latn' : 'en-US-u-nu-latn', {
    compactDisplay: 'short',
    maximumFractionDigits: 1,
    notation: 'compact',
  });
}

export interface TrendChartData {
  readonly categories: readonly string[];
  readonly series: readonly ChartSeries[];
}

/**
 * Pure mapper from the Dashboard trends payload to chart-ready rows. Kept separate from the component
 * so currency grouping, sparse periods, and amount formatting are unit-testable without rendering.
 */
export function toTrendSeries(
  trends: DashboardTrends,
  metric: TrendMetric,
  language: MoneyLanguage & TimeLanguage,
): TrendChartData {
  const periods = [...new Set(trends.points.map((point) => point.periodStart))].sort();
  const currencies = [...new Set(trends.points.map((point) => point.currencyCode ?? 'ALL'))];
  const numberFormat = countFormat(language);

  const series = currencies.map((currencyCode) => {
    const byPeriod = new Map(
      trends.points
        .filter((point) => (point.currencyCode ?? 'ALL') === currencyCode)
        .map((point) => [point.periodStart, point] as const),
    );

    const values: number[] = [];
    const labels: string[] = [];
    for (const period of periods) {
      const point = byPeriod.get(period);
      if (metric === 'transactionCount') {
        const count = point?.transactionCount ?? 0;
        values.push(count);
        labels.push(numberFormat.format(count));
        continue;
      }
      // Number() drives pixel geometry only; the label formats the API decimal at Dashboard precision.
      const amount = point?.playAmount ?? '0';
      values.push(Number(amount));
      labels.push(formatMoney(amount, currencyCode, language));
    }

    return { labels, name: currencyCode, values };
  });

  return {
    categories: periods.map(
      (period) => formatTimestamp(period, trends.meta.timezone, language).display,
    ),
    series,
  };
}
